import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  Stack,
} from '@mui/material';
import {
  Home as HomeIcon,
  Business as BusinessIcon,
  Visibility as ViewIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as InProgressIcon,
  RocketLaunch as RocketIcon,
  Computer as LocalIcon,
  CloudQueue as RemoteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import DeploymentService from '../services/DeploymentService';
import { DeploymentResult } from '../models/Deployment';
import ManageDeployDialog from '../components/deploy/ManageDeployDialog';

/** 진행 중으로 간주하는 status 집합 — 이 중 하나라도 있으면 새 배포 시작 차단. */
const IN_PROGRESS_STATUSES = new Set(['in_progress', 'waiting', 'dispatching', 'running']);

/** site 값이 'company' 이거나 비어있으면 로컬, 그 외(banpo/osong/central 등) 는 원격으로 분류. */
function isLocalDeployment(d: DeploymentResult): boolean {
  const site = (d.site || '').toLowerCase();
  return site === '' || site === 'company';
}

const DeploymentResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [deployments, setDeployments] = useState<DeploymentResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // manage-deploy-versioning: dialog visibility + post-trigger notification
  const [manageDeployOpen, setManageDeployOpen] = useState(false);
  const [triggerSnackbar, setTriggerSnackbar] = useState<string | null>(null);

  const selectedProject = localStorage.getItem('selectedProject');
  const projectData = selectedProject ? JSON.parse(selectedProject) : null;

  const fetchDeployments = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await DeploymentService.getDeployments(projectId);
      if (response.success) {
        setDeployments(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('배포 결과를 불러오는 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    fetchDeployments();
  }, [fetchDeployments]);

  /** 진행 중 배포 — 새 배포 시작을 차단하는 핵심 신호. */
  const inProgressDeployments = useMemo(
    () => deployments.filter((d) => IN_PROGRESS_STATUSES.has(d.status)),
    [deployments],
  );
  const hasInProgress = inProgressDeployments.length > 0;

  /** 진행 중 배포가 있는 동안은 10초마다 폴링 → 끝나면 상태 자동 반영 → 버튼 풀림. */
  useEffect(() => {
    if (!hasInProgress) return;
    const timer = window.setInterval(() => {
      fetchDeployments();
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [hasInProgress, fetchDeployments]);

  /** 로컬(company)/원격(remote) 두 그룹으로 분리. 시각 desc 정렬은 백엔드가 보장한다고 가정. */
  const localDeployments = useMemo(
    () => deployments.filter(isLocalDeployment),
    [deployments],
  );
  const remoteDeployments = useMemo(
    () => deployments.filter((d) => !isLocalDeployment(d)),
    [deployments],
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <SuccessIcon sx={{ color: 'success.main' }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'in_progress':
        return <InProgressIcon sx={{ color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const getStatusChip = (status: string) => {
    const statusMap: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' }> = {
      success: { label: '성공', color: 'success' },
      failed: { label: '실패', color: 'error' },
      in_progress: { label: '진행중', color: 'warning' },
      waiting: { label: '대기', color: 'info' },
      dispatching: { label: '디스패치', color: 'warning' },
      running: { label: '실행중', color: 'warning' },
      timeout: { label: '타임아웃', color: 'error' },
      skipped: { label: '스킵', color: 'default' },
    };
    const statusInfo = statusMap[status] || { label: status, color: 'default' as const };
    return <Chip label={statusInfo.label} color={statusInfo.color} size="small" />;
  };

  const getEnvironmentChip = (environment: string) => {
    const envMap = {
      production: { label: '운영', color: 'error' as const },
      staging: { label: '스테이징', color: 'warning' as const },
      development: { label: '개발', color: 'info' as const },
    };
    const envInfo = envMap[environment as keyof typeof envMap] || { label: environment, color: 'default' as const };
    return <Chip label={envInfo.label} color={envInfo.color} size="small" variant="outlined" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewDetail = (deploymentId: number) => {
    navigate(`/project/${projectId}/deployments/${deploymentId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            배포 결과를 불러오는 중...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Breadcrumb Navigation */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            프로젝트 선택
          </Link>
          <Link
            color="inherit"
            onClick={() => navigate(`/project/${projectId}`)}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {projectData?.name || `프로젝트 ${projectId}`}
          </Link>
          <Typography color="text.primary">배포 결과</Typography>
        </Breadcrumbs>
      </Box>

      {/* Page Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              🚀 배포 결과
            </Typography>
            <Typography variant="body1" color="text.secondary">
              프로젝트의 배포 이력을 확인하고 관리합니다
            </Typography>
          </Box>
          {/* manage-deploy-versioning: 관리 서비스(프론트/백엔드) 직접 배포 진입점.
              진행 중 배포가 있으면 중복 트리거 방지를 위해 잠금 + 스피너 표시. */}
          <Tooltip
            title={
              hasInProgress
                ? `진행 중인 배포가 ${inProgressDeployments.length}건 있습니다. 완료 후 다시 시도하세요.`
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  hasInProgress ? (
                    <CircularProgress size={18} sx={{ color: 'inherit' }} />
                  ) : (
                    <RocketIcon />
                  )
                }
                onClick={() => setManageDeployOpen(true)}
                disabled={hasInProgress}
                sx={{ flexShrink: 0 }}
              >
                {hasInProgress ? '배포 진행 중...' : '관리 서비스 배포'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {projectId && (
        <ManageDeployDialog
          open={manageDeployOpen}
          onClose={() => setManageDeployOpen(false)}
          projectId={projectId}
          onTriggered={(deploymentId) => {
            setTriggerSnackbar(`배포가 시작되었습니다 (ID: ${deploymentId})`);
            // 새 row 가 in_progress 로 들어오므로 목록을 즉시 새로고침
            DeploymentService.getDeployments(projectId)
              .then((res) => {
                if (res.success) setDeployments(res.data);
              })
              .catch(() => {
                /* swallow — snackbar 로 이미 트리거 사실은 알림 */
              });
          }}
        />
      )}

      <Snackbar
        open={Boolean(triggerSnackbar)}
        autoHideDuration={5000}
        onClose={() => setTriggerSnackbar(null)}
        message={triggerSnackbar ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* 로컬(company) / 원격(remote) 배포 이력을 별도 섹션으로. 운영자가
          어떤 호스트로 나갔는지 한눈에 분리. site 컬럼이 비어있는 legacy 행은 로컬로. */}
      <DeploymentSection
        title="로컬 (company) 배포 이력"
        subtitle="회사 서버(192.168.0.102) 또는 site 정보 없는 legacy 배포"
        icon={<LocalIcon color="primary" />}
        deployments={localDeployments}
        showSite={false}
        getStatusIcon={getStatusIcon}
        getStatusChip={getStatusChip}
        getEnvironmentChip={getEnvironmentChip}
        formatDate={formatDate}
        onView={handleViewDetail}
      />

      <Box sx={{ height: 24 }} />

      <DeploymentSection
        title="원격 (remote) 배포 이력"
        subtitle="banpo / osong / central 등 현장 서버로 직접 배포된 이력"
        icon={<RemoteIcon color="secondary" />}
        deployments={remoteDeployments}
        showSite={true}
        getStatusIcon={getStatusIcon}
        getStatusChip={getStatusChip}
        getEnvironmentChip={getEnvironmentChip}
        formatDate={formatDate}
        onView={handleViewDetail}
      />
    </Container>
  );
};

interface DeploymentSectionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  deployments: DeploymentResult[];
  /** 로컬 섹션은 site 컬럼을 숨기고, 원격 섹션은 노출 */
  showSite: boolean;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusChip: (status: string) => React.ReactNode;
  getEnvironmentChip: (env: string) => React.ReactNode;
  formatDate: (iso: string) => string;
  onView: (deploymentId: number) => void;
}

const DeploymentSection: React.FC<DeploymentSectionProps> = ({
  title,
  subtitle,
  icon,
  deployments,
  showSite,
  getStatusIcon,
  getStatusChip,
  getEnvironmentChip,
  formatDate,
  onView,
}) => {
  const colSpan = showSite ? 9 : 8;
  return (
    <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden', borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, py: 1.75, bgcolor: 'action.hover' }}>
        {icon}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({deployments.length}건)
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>상태</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>서비스</TableCell>
              {showSite && <TableCell sx={{ fontWeight: 'bold' }}>사이트</TableCell>}
              <TableCell sx={{ fontWeight: 'bold' }}>버전</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>환경</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>배포 요청자</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>브랜치</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>배포일시</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deployments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    이 그룹의 배포 이력이 없습니다.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              deployments.map((deployment) => (
                <TableRow
                  key={deployment.id}
                  hover
                  sx={{ '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' } }}
                  onClick={() => onView(deployment.id)}
                >
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      {getStatusIcon(deployment.status)}
                      {getStatusChip(deployment.status)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {deployment.appname ? (
                      <Chip label={deployment.appname} size="small" variant="outlined" />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  {showSite && (
                    <TableCell>
                      {deployment.site ? (
                        <Chip label={deployment.site} size="small" color="secondary" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {deployment.version}
                      </Typography>
                      {deployment.deploy_mode === 'reuse' && (
                        <Chip label="reuse" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{getEnvironmentChip(deployment.environment)}</TableCell>
                  <TableCell>{deployment.deployed_by || '-'}</TableCell>
                  <TableCell>
                    <Chip label={deployment.branch} size="small" />
                  </TableCell>
                  <TableCell>{formatDate(deployment.deployed_at)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="상세보기">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(deployment.id);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DeploymentResultsPage;
